/**
 * Keycloak bootstrap + auth context.
 *
 * Boot order, and why:
 *   1. GET /config.json          — we cannot construct Keycloak without it
 *   2. keycloak.init()           — login-required, so an anonymous visitor is
 *                                  redirected out of the app immediately
 *   3. GET /api/me               — identity, company, entitlements
 *
 * Nothing renders until all three succeed, which means every child component
 * can treat `me` as present and never has to write a loading branch.
 *
 * `checkLoginIframe: false` is not optional. With the login iframe off, no app
 * needs to be framed, which is what lets the router send
 * `frame-ancestors 'none'` and `X-Frame-Options: DENY` without breaking login.
 * Login is a top-level redirect, which CSP does not restrict.
 */
import Keycloak from "keycloak-js";
import {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { loadConfig, type PlatformConfig } from "./config";
import { createApi, ApiError, type ApiClient } from "./api";
import type { Me } from "./types";
import { Logo } from "@cqt/brand";

interface AuthContextValue {
  config: PlatformConfig;
  api: ApiClient;
  me: Me;
  /** Re-fetch /me — call after a grant changes so tiles update without F5. */
  refreshMe: () => Promise<void>;
  logout: () => void;
  /** Start Keycloak's passkey registration action for the signed-in user. */
  registerPasskey: () => Promise<void>;
  accountUrl: string;
}

const AuthContext = createContext<AuthContextValue | null>(null);

type BootState =
  | { phase: "loading"; label: string }
  | { phase: "error"; label: string; detail: string }
  | { phase: "ready"; value: AuthContextValue };

/** Full-page brand splash. Uses the dark plate in both themes — it is the same
 *  surface the logo is drawn for. */
function Splash({
  children,
  spinner = false,
}: {
  children: ReactNode;
  spinner?: boolean;
}) {
  return (
    <div
      className="plate"
      style={{
        minHeight: "100dvh",
        display: "grid",
        placeItems: "center",
        padding: 24,
        textAlign: "center",
      }}
    >
      <div style={{ display: "grid", justifyItems: "center", gap: 20, maxWidth: 460 }}>
        <Logo size={148} />
        {spinner ? <div className="spinner" aria-hidden="true" /> : null}
        {children}
      </div>
    </div>
  );
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<BootState>({ phase: "loading", label: "Starting" });
  // Keycloak.init() must run exactly once. React 19 StrictMode double-invokes
  // effects in development, and a second init() throws.
  const booted = useRef(false);

  useEffect(() => {
    if (booted.current) return;
    booted.current = true;

    (async () => {
      let config: PlatformConfig;
      try {
        setState({ phase: "loading", label: "Loading configuration" });
        config = await loadConfig();
      } catch (err) {
        setState({
          phase: "error",
          label: "Configuration unavailable",
          detail:
            "The platform could not read /config.json. If you are running locally, " +
            "check that the router is up. " +
            (err instanceof Error ? err.message : String(err)),
        });
        return;
      }

      const kc = new Keycloak({
        url: config.keycloakUrl,
        realm: config.realm,
        clientId: config.clientId,
      });

      try {
        setState({ phase: "loading", label: "Signing in" });
        await kc.init({
          onLoad: "login-required",
          pkceMethod: "S256",
          checkLoginIframe: false,
        });
      } catch (err) {
        setState({
          phase: "error",
          label: "Sign-in unavailable",
          detail:
            `Could not reach Keycloak at ${config.keycloakUrl} (realm "${config.realm}"). ` +
            (err instanceof Error ? err.message : String(err)),
        });
        return;
      }

      // Refresh with 30s of headroom so a request never carries a token that
      // expires in flight. updateToken() is a no-op when the token is fresh.
      const getToken = async () => {
        try {
          await kc.updateToken(30);
        } catch {
          // The refresh token is gone or the session ended: start over.
          kc.login();
          throw new ApiError(401, "Session expired");
        }
        return kc.token ?? "";
      };

      const api = createApi(config.apiBase, getToken);

      const fetchMe = () => api.get<Me>("/me");

      let me: Me;
      try {
        setState({ phase: "loading", label: "Loading your account" });
        me = await fetchMe();
      } catch (err) {
        const detail =
          err instanceof ApiError && err.status === 403
            ? "Your Keycloak login worked, but this account is not provisioned on the platform yet. " +
              "An administrator needs to create your user record."
            : err instanceof Error
              ? err.message
              : String(err);
        setState({ phase: "error", label: "Account not available", detail });
        return;
      }

      const value: AuthContextValue = {
        config,
        api,
        me,
        refreshMe: async () => {
          const next = await fetchMe();
          setState((prev) =>
            prev.phase === "ready"
              ? { phase: "ready", value: { ...prev.value, me: next } }
              : prev,
          );
        },
        logout: () => kc.logout({ redirectUri: window.location.origin }),
        registerPasskey: () =>
          kc.login({
            action: "webauthn-register-passwordless",
            redirectUri: window.location.href,
          }),
        accountUrl: `${config.keycloakUrl}/realms/${encodeURIComponent(config.realm)}/account`,
      };

      setState({ phase: "ready", value });
    })();
  }, []);

  if (state.phase === "loading") {
    return (
      <Splash spinner>
        <p className="meta" style={{ letterSpacing: "0.2em", textTransform: "uppercase" }}>
          {state.label}
        </p>
      </Splash>
    );
  }

  if (state.phase === "error") {
    return (
      <Splash>
        <h1 style={{ color: "var(--cqt-bone)", fontSize: "1.4rem" }}>{state.label}</h1>
        <p style={{ color: "var(--cqt-ash-2)", fontSize: "0.92rem", lineHeight: 1.55 }}>
          {state.detail}
        </p>
        <button className="btn btn--primary" onClick={() => window.location.reload()}>
          Try again
        </button>
      </Splash>
    );
  }

  return <AuthContext.Provider value={state.value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside <AuthProvider>");
  return ctx;
}

/** Convenience: the current user's level on a tool, or "none". */
export function useToolLevel(toolKey: string) {
  const { me } = useAuth();
  return me.tools.find((t) => t.key === toolKey)?.level ?? "none";
}

/** Stable callback identity for React Query keys etc. */
export function useApi(): ApiClient {
  return useAuth().api;
}

export function useMe(): Me {
  return useAuth().me;
}
